import { useState, useEffect } from "react";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { AtSign, Send } from "lucide-react";
import { toast } from "sonner";

import { Checkbox } from "@/components/ui/checkbox";
import { getLanguage } from "@/utils/languageUtils";
import { leadUILabels, commonMiscLabels, commonButtonLabels } from "@/i18n";
import { AdminForMention } from "@/services/admin/adminService";
import { useAdmin } from "@/hooks/useAdmin";

interface CommentFormProps {
  onSubmit: (text: string, isPublic: boolean, mentions: string[]) => void;
  isSubmitting?: boolean;
  dense?: boolean;
}

export const CommentForm = ({
  onSubmit,
  isSubmitting = false,
  dense = false,
}: CommentFormProps) => {
  const [newComment, setNewComment] = useState("");
  const [isPublic, setIsPublic] = useState(false);
  const [showMentions, setShowMentions] = useState(false);
  const [mentions, setMentions] = useState<string[]>([]);

  const uiLabels = leadUILabels(getLanguage());
  const commonLabels = commonMiscLabels(getLanguage());
  const commonButtons = commonButtonLabels(getLanguage());

  // Load admins for mentions via hook
  const { adminsForMentions } = useAdmin();
  const colleagues = adminsForMentions.data;
  const isLoadingColleagues = adminsForMentions.isLoading;

  const handleSubmit = (e?: React.FormEvent) => {
    e?.preventDefault();
    if (!newComment.trim()) return;

    onSubmit(newComment, isPublic, mentions);

    // Show toast with mentioned admin names
    if (mentions.length > 0) {
      const mentionedNames = mentions
        .map((actorId) => colleagues.find((c) => c.id === actorId)?.name)
        .filter(Boolean)
        .join(", ");
      toast.success(uiLabels.notificationsSent, {
        description: uiLabels.notificationsSentDescription.replace(
          "{names}",
          mentionedNames,
        ),
      });
    }

    setNewComment("");
    setIsPublic(false);
    setMentions([]);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "@") {
      setShowMentions(true);
    }

    // Enter to send, Shift+Enter for newline
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      if (!isSubmitting && newComment.trim()) {
        handleSubmit();
      }
    }
  };

  const insertMention = (colleague: AdminForMention) => {
    setNewComment((prev) => `${prev}@${colleague.name} `);
    setMentions((prev) => {
      // Avoid duplicates
      if (prev.includes(colleague.id)) {
        return prev;
      }
      return [...prev, colleague.id];
    });
    setShowMentions(false);
  };

  return (
    <form onSubmit={handleSubmit} className={dense ? "space-y-1" : "space-y-3"}>
      <div className="relative">
        <Textarea
          value={newComment}
          onChange={(e) => setNewComment(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={uiLabels.commentPlaceholder}
          rows={dense ? 2 : undefined}
          className={
            dense
              ? "min-h-[44px] max-h-[72px] resize-none text-xs py-1.5 leading-snug"
              : "min-h-[80px] resize-none"
          }
        />
        {showMentions && (
          <div className="absolute bottom-full left-0 bg-card border rounded-md shadow-lg p-2 space-y-1 w-64 z-10 max-h-64 overflow-y-auto">
            {isLoadingColleagues ? (
              <div className="px-2 py-1 text-sm text-muted-foreground">
                {uiLabels.loadingColleagues}
              </div>
            ) : colleagues.length === 0 ? (
              <div className="px-2 py-1 text-sm text-muted-foreground">
                {uiLabels.noColleaguesAvailable}
              </div>
            ) : (
              colleagues.map((colleague) => (
                <button
                  key={colleague.id}
                  type="button"
                  onClick={() => insertMention(colleague)}
                  className="w-full hover:bg-accent/20 text-left px-2 py-1 rounded"
                >
                  {colleague.name}
                </button>
              ))
            )}
          </div>
        )}
      </div>
      <div className="flex justify-between items-center gap-1">
        <div className="flex items-center space-x-1.5 min-w-0">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => setShowMentions(!showMentions)}
            className={`hover:bg-accent/20 shrink-0 ${dense ? "h-7 px-2" : ""}`}
            disabled={isLoadingColleagues}
          >
            <AtSign className={dense ? "h-3.5 w-3.5" : undefined} />
            {!dense && <p className="">{commonLabels.notifyColleagues}</p>}
            {mentions.length > 0 && (
              <span
                className={`bg-primary/20 text-primary rounded-full text-xs ${dense ? "ml-1 px-1.5 py-0" : "ml-2 px-2 py-0.5"}`}
              >
                {mentions.length}
              </span>
            )}
          </Button>
          {/* TODO: Bortkommenterat för nu. Vad menas ens med Offentlig kommentar? */}
          {/* <div className="flex items-center space-x-2">
            <Checkbox
              id="public"
              checked={isPublic}
              onCheckedChange={(checked) => setIsPublic(checked as boolean)}
            />
            <label
              htmlFor="public"
              className="text-xs text-muted-foreground cursor-pointer"
            >
              Offentlig kommentar
            </label>
          </div> */}
        </div>
        <Button
          type="submit"
          size="sm"
          className={dense ? "h-7 px-2 shrink-0" : undefined}
          disabled={!newComment.trim() || isSubmitting}
        >
          <Send className={dense ? "h-3.5 w-3.5" : undefined} />
          {!dense && (isSubmitting ? commonButtons.sending : commonButtons.send)}
        </Button>
      </div>
    </form>
  );
};
